<script setup lang="ts">
import { useForm } from "@inertiajs/vue3"
import { computed, watch } from "vue"
import route from "ziggy-js"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/components/ui/toast/use-toast"

type Employee = {
    id: number
    name: string
    title: string
    branch_id: number | null
}

type Shift = {
    id: number
    station: string
    employee: Employee
    start: string
    end: string
    is_holiday: boolean
}

type StoreHours = {
    default_start: string
    default_end: string
    opens_label: string
    closes_label: string
    opening_login_label: string
}

const props = defineProps<{
    open: boolean
    date: string
    station: string
    employees: Employee[]
    storeHours: StoreHours
    shift?: Shift | null
    employeeId?: number | null
    storePath: string
}>()

const emit = defineEmits<{
    "update:open": [value: boolean]
}>()

const { toast } = useToast()

const isEditing = computed(() => Boolean(props.shift))

const form = useForm({
    date: props.date,
    employee_id: "",
    station: props.station,
    time_start: props.storeHours.default_start,
    time_end: props.storeHours.default_end,
    is_holiday: false,
})

const stationLabel = computed(() => {
    return {
        barista: "Barista",
        cashier: "Cashier",
        kitchen: "Kitchen",
    }[props.station] ?? props.station
})

watch(
    () => [props.open, props.shift, props.station, props.date, props.employeeId],
    () => {
        if (!props.open) {
            return
        }

        form.clearErrors()

        if (props.shift) {
            form.date = props.date
            form.employee_id = String(props.shift.employee.id)
            form.station = props.shift.station
            form.time_start = props.shift.start
            form.time_end = props.shift.end
            form.is_holiday = props.shift.is_holiday
            return
        }

        form.reset()
        form.date = props.date
        form.employee_id = props.employeeId ? String(props.employeeId) : ""
        form.station = props.station
        form.time_start = props.storeHours.default_start
        form.time_end = props.storeHours.default_end
        form.is_holiday = false
    }
)

function close() {
    emit("update:open", false)
}

function submit() {
    const options = {
        preserveScroll: true,
        onSuccess() {
            close()
            toast({
                description: isEditing.value ? "Shift updated." : "Employee assigned.",
            })
        },
    }

    if (isEditing.value && props.shift) {
        form.put(route("shifts.update", props.shift.id), options)
        return
    }

    form.post(props.storePath, options)
}
</script>

<template>
    <Dialog :open="open" @update:open="emit('update:open', $event)">
        <DialogContent class="max-w-md">
            <form class="space-y-4" @submit.prevent="submit">
                <DialogHeader>
                    <DialogTitle>{{ isEditing ? "Edit shift" : `Assign to ${stationLabel}` }}</DialogTitle>
                    <DialogDescription>
                        Store hours are {{ storeHours.opens_label }} to {{ storeHours.closes_label }}. Staff must log
                        in 15 minutes early for opening ({{ storeHours.opening_login_label }}). Later is late.
                    </DialogDescription>
                </DialogHeader>

                <div class="space-y-2">
                    <Label>Employee</Label>
                    <Select v-model:model-value="form.employee_id">
                        <SelectTrigger>
                            <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem v-for="employee in employees" :key="employee.id" :value="String(employee.id)">
                                {{ employee.name }}
                                <span class="text-muted-foreground"> · {{ employee.title }}</span>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <p v-if="form.errors.employee_id" class="text-sm text-destructive">{{ form.errors.employee_id }}</p>
                </div>

                <div class="space-y-2">
                    <Label>Station</Label>
                    <Select v-model:model-value="form.station">
                        <SelectTrigger>
                            <SelectValue placeholder="Select station" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="barista">Barista</SelectItem>
                            <SelectItem value="cashier">Cashier</SelectItem>
                            <SelectItem value="kitchen">Kitchen</SelectItem>
                        </SelectContent>
                    </Select>
                    <p v-if="form.errors.station" class="text-sm text-destructive">{{ form.errors.station }}</p>
                </div>

                <div class="grid grid-cols-2 gap-3">
                    <div class="space-y-2">
                        <Label>Start</Label>
                        <Input v-model="form.time_start" type="time" />
                        <p v-if="form.errors.time_start" class="text-sm text-destructive">{{ form.errors.time_start }}</p>
                    </div>
                    <div class="space-y-2">
                        <Label>End</Label>
                        <Input v-model="form.time_end" type="time" />
                        <p v-if="form.errors.time_end" class="text-sm text-destructive">{{ form.errors.time_end }}</p>
                    </div>
                </div>

                <Label class="flex items-center gap-2 text-sm font-normal">
                    <Switch :checked="form.is_holiday" @update:checked="form.is_holiday = $event" />
                    Holiday rate (+30%)
                </Label>

                <DialogFooter>
                    <Button type="button" variant="outline" @click="close">Cancel</Button>
                    <Button type="submit" :disabled="form.processing">
                        {{ isEditing ? "Save changes" : "Assign" }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
